// src/pages/account/ForgotPassword.js
import React, { useState, useContext, useCallback, useRef, useEffect, Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import './ForgotPassword.css'; // We'll create this CSS file
import { NotificationContext } from '../../context/NotificationContext';
import { BASE_URL } from '../../utils/config';
import { useLanguage } from '../../hooks/useLanguage';
import { IS_PRERENDERING } from '../../utils/config';
import { useRecaptcha } from '../../hooks/useRecaptcha'; // Import the hook

const ReCAPTCHA = lazy(() => import('react-google-recaptcha'));

const ForgotPassword = () => {
  const { t } = useTranslation();
  const lang = useLanguage();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { addNotification } = useContext(NotificationContext);
  const handleVerifyRef = useRef(null); // Create a ref
  const [error, setError] = useState(null);

  // If an error was set from an async callback, throw it during render to be caught by the Error Boundary
  if (error) {
    throw error;
  }

  const handleRecaptchaExpire = useCallback(() => {
    setIsSubmitting(false);
    addNotification(t('recaptcha.expired'), 'warning');
  }, [addNotification, t]);

  const handleRecaptchaError = useCallback(
    (err) => {
      setIsSubmitting(false);
      addNotification(t('recaptcha.loadError'), 'error', 7000);
      setError(new Error(t('recaptcha.loadError'))); // Propagate error
    },
    [addNotification, t, setError]
  );

  const { recaptchaRef, execute, onChange, reset, onErrored, onExpired } = useRecaptcha((token) => handleVerifyRef.current(token), handleRecaptchaExpire, handleRecaptchaError);

  const handleVerify = useCallback(
    async (recaptchaToken) => {
      try {
        // Notification is now in handleSubmit
        const response = await fetch(`${BASE_URL}/authUser/forgot-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, recaptchaToken }),
        });

        const data = await response.json();

        if (response.ok) {
          // The backend sends a generic success message for security reasons
          addNotification(data.message || t('forgotPasswordPage.successMessage', { email }), 'success', 7000);
          setEmail(''); // Clear email field on success
        } else {
          reset(); // Reset reCAPTCHA on failure
          // Even for errors, the backend might send a generic message, or a specific one if it's not a "user not found" type of error.
          addNotification(data.message || t('forgotPasswordPage.failureMessage'), 'error', 5000);
        }
      } catch (error) {
        reset(); // Also reset on network errors
        setError(error);
      } finally {
        setIsSubmitting(false);
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [email, addNotification, reset, setError, t]
  );

  // Keep the ref updated with the latest version of the handler.
  useEffect(() => {
    handleVerifyRef.current = handleVerify;
  }, [handleVerify]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email) {
      addNotification(t('forgotPasswordPage.emailRequired'), 'warning');
      return;
    }
    if (IS_PRERENDERING) {
      addNotification('Form submission is disabled during page generation.', 'info');
      return;
    }
    addNotification(t('forgotPasswordPage.sendingRequest'), 'info');
    setIsSubmitting(true);
    execute();
  };

  return (
    <div className="forgot-password-page-container">
      <meta name="robots" content="noindex" />
      <div className="forgot-password-container">
        <h2>{t('forgotPasswordPage.title')}</h2>
        <p>{t('forgotPasswordPage.description')}</p>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">{t('forgotPasswordPage.emailLabel')}</label>
            <input type="email" id="email" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={isSubmitting} autoComplete="email" />
          </div>
          <button type="submit" className="submit-btn" disabled={isSubmitting || IS_PRERENDERING}>
            {isSubmitting ? t('forgotPasswordPage.sendingButton') : t('forgotPasswordPage.sendButton')}
          </button>
          {!IS_PRERENDERING && (
            <Suspense fallback={null}>
              <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY} onChange={onChange} onErrored={onErrored} onExpired={onExpired} />
            </Suspense>
          )}
        </form>
        <div className="login-link">
          <Link to={`/${lang}/login`}>{t('forgotPasswordPage.backToLogin')}</Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
